import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/services", label: "Services", testid: "nav-link-services" },
  { to: "/#method", label: "Our Approach", testid: "nav-link-approach" },
  { to: "/#work", label: "Case Studies", testid: "nav-link-case-studies" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const isActive = (to) => {
    const base = to.split("#")[0];
    return location.pathname === base && !to.includes("#");
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? "border-b border-white/5 bg-[#080808]/85 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link
          to="/"
          data-testid="nav-logo"
          aria-label="4EMPIRE — home"
          className="font-anton text-2xl tracking-tight"
        >
          <span className="text-white">4</span>
          <span className="text-empire-red">EMPIRE</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <Link
              key={l.testid}
              to={l.to}
              data-testid={l.testid}
              className={`font-grotesk text-xs uppercase tracking-[0.18em] transition-colors hover:text-white ${
                isActive(l.to) ? "text-white" : "text-empire-mist"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="bg-empire-red px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.18em] text-white transition-colors hover:bg-empire-glow"
          >
            Let&apos;s Talk
          </Link>
        </nav>

        <button
          type="button"
          data-testid="nav-menu-toggle"
          className="text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <nav
          data-testid="nav-mobile-menu"
          className="border-t border-white/5 bg-[#080808]/95 px-6 py-6 backdrop-blur-md lg:hidden"
          aria-label="Mobile"
        >
          <div className="flex flex-col gap-5">
            {LINKS.map((l) => (
              <Link
                key={l.testid}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                className="font-grotesk text-sm uppercase tracking-[0.18em] text-empire-mist transition-colors hover:text-white"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-cta-button"
              className="mt-2 w-fit bg-empire-red px-6 py-3 font-grotesk text-xs uppercase tracking-[0.18em] text-white transition-colors hover:bg-empire-glow"
            >
              Let&apos;s Talk
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
